
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, TrendingUp, TrendingDown } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface PriceTargetsCardProps {
  priceTargets: {
    bullish: number; // percentage
    bearish: number; // percentage (negative)
  };
  confidence: number;
}

const formatPercentage = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

export const PriceTargetsCard = ({ priceTargets, confidence }: PriceTargetsCardProps) => {
  const { t } = useTranslation();
  
  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-emerald-500/5 border border-border/50 backdrop-blur-sm hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300 hover:-translate-y-1 group card-shine soft-glow">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_40%,rgba(34,197,94,0.08),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute inset-0 shimmer-slide opacity-0 group-hover:opacity-30 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <CardHeader className="relative z-10 pb-4">
        <CardTitle className="text-foreground flex items-center gap-2 group-hover:text-emerald-600 transition-colors duration-300">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500/20 transition-all duration-300 floating-action">
            <Target className="h-5 w-5 icon-spin-hover" />
          </div>
          {t('market_analysis.volatility_potential')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent rounded-xl hover:from-green-500/20 hover:via-green-500/10 hover:to-green-500/5 transition-all duration-300 backdrop-blur-sm border border-green-500/20 hover:border-green-500/30 group/bullish card-hover-effect gradient-animate">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-green-500 floating-action icon-spin-hover" />
            </div>
            <div className="text-2xl font-bold text-green-500 group-hover/bullish:scale-110 transition-transform duration-300 hover-scale">
              {formatPercentage(priceTargets.bullish)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">{t('market_analysis.bullish')}</div>
          </div>
          
          <div className="text-center p-4 bg-gradient-to-br from-red-500/10 via-red-500/5 to-transparent rounded-xl hover:from-red-500/20 hover:via-red-500/10 hover:to-red-500/5 transition-all duration-300 backdrop-blur-sm border border-red-500/20 hover:border-red-500/30 group/bearish card-hover-effect gradient-animate">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingDown className="h-4 w-4 text-red-500 floating-action icon-spin-hover" />
            </div>
            <div className="text-2xl font-bold text-red-500 group-hover/bearish:scale-110 transition-transform duration-300 hover-scale">
              {formatPercentage(priceTargets.bearish)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">{t('market_analysis.bearish')}</div>
          </div>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-r from-accent/30 via-accent/20 to-transparent rounded-xl hover:from-accent/50 hover:via-accent/30 hover:to-accent/10 transition-all duration-300 backdrop-blur-sm border border-border/20 hover:border-border/40 card-hover-effect backdrop-blur-subtle">
          <div className="text-sm text-muted-foreground mb-1">{t('market_analysis.confidence_level')}</div>
          <div className="text-xl font-bold text-emerald-500 flex items-center justify-center gap-2">
            <span className="hover-scale">{confidence}%</span>
            <div className="w-16 bg-muted/50 rounded-full h-2 floating-action">
              <div 
                className="h-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500 pulse-soft"
                style={{ 
                  width: `${confidence}%`,
                  willChange: 'width'
                }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
